<template>
  <div id="client-list">
    <b-container>
      <h1>{{ $t('CLIENT_LIST') }} ({{ total }})</h1>

      <div class="filters">
        <text-input
          :placeholder="$t('SEARCH_NAME_ID_CARD_PHONE_NUMBER')"
          v-model="searchInput"
          @keyup.enter="search"
        >
          <template v-slot:icon>
            <font-awesome-icon class="icon" :icon="faSearch" />
          </template>
        </text-input>
      </div>

      <b-row class="list">
        <b-col
          xs="12" sm="6" lg="4"
          v-for="client in items"
          :key="client.id"
        >
          <div class="item">
            <router-link :to="applyParams(PATHS.CLIENT_DETAIL, client.finfluxId)">
              <h3>
                <span>{{ client.nameVi }}</span>
                <span>#{{client.finfluxId}}</span>
              </h3>
            </router-link>
            <div class="info">
              <table>
                <tr>
                  <td>{{ $t('CITIZEN_ID') }}:</td>
                  <td>{{ client.citizenId }}</td>
                </tr>
                <tr>
                  <td>{{ $t('PHONE_NUMBER') }}:</td>
                  <td>{{ client.primaryPhoneNumber }}</td>
                </tr>
                <tr>
                  <td>{{ $t('BRANCH') }}:</td>
                  <td>{{ getBranchName(client.branchId) }}</td>
                </tr>
                <tr>
                  <td>{{ $t('NUMBER_OF_ACTIVE_CONTRACTS') }}:</td>
                  <td>{{ client.numberOfActiveContracts }}/{{ client.numberOfContracts }}</td>
                </tr>
              </table>
            </div>

            <div class="call">
              <font-awesome-icon class="icon" :icon="faPhoneAlt" @click="startCall(client)"/>
            </div>
          </div>
        </b-col>
      </b-row>

      <pagination
        :page="filters.page"
        :pageSize="filters.pageSize"
        :total="total"
        @pageSizeChange="changePageSize"
        @pageChange="changePage"
      />
    </b-container>
  </div>
</template>

<script>
import { onMounted, ref, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSearch, faPhoneAlt } from '@fortawesome/free-solid-svg-icons';

import TextInput from '@/components/TextInput';
import Pagination from '@/components/Pagination';

import { CALL_REFS, PATHS } from '@/consts';
import { applyParams } from '@/helpers';

const { useState, useActions } = createNamespacedHelpers('clientList');
const { useState: useAppState } = createNamespacedHelpers('app');
const { useActions: usePhoneActions } = createNamespacedHelpers('phone');

export default {
  name: 'ClientList',
  components: {
    Pagination,
    TextInput,
    FontAwesomeIcon
  },
  metaInfo() {
    const { $i18n } = this;
    const title = $i18n.t('CLIENT_LIST');

    return {
      title,
      meta: []
    };
  },
  setup() {
    const { isDesktop, branches } = useAppState(['isDesktop', 'branches']);
    const { filters, items, total } = useState(['filters', 'items', 'total']);
    const { applyFilter, getClientList } = useActions(['applyFilter', 'getClientList']);
    const { startOutboundCall, setRef } = usePhoneActions(['startOutboundCall', 'setRef']);

    const searchInput = ref(filters.value.term);

    const search = () => {
      applyFilter({
        term: searchInput.value,
      });
    };

    const changePageSize = (value) => {
      applyFilter({
        pageSize: value,
        page: 1,
      });
    };

    const changePage = (value) => {
      applyFilter({
        page: value,
      });
    };

    const getBranchName = (branchId) => {
      const branch = branches.value.find((b) => b.id === branchId);
      return branch.name || '';
    };

    const startCall = (client) => {
      setRef({ type: CALL_REFS.CLIENT, refId: client.id });
      startOutboundCall(client.primaryPhoneNumber);
    };

    onMounted(() => {
      getClientList();
    });

    watch(filters, () => {
      getClientList();
    });

    return {
      searchInput,
      search,
      changePageSize,
      changePage,
      filters,
      items,
      total,
      faSearch,
      faPhoneAlt,
      startCall,
      isDesktop,
      getBranchName,
      applyParams,
      PATHS
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#client-list {
  padding: 30px;

  .container {
    margin-left: 0;
  }

  h1 {
    font-size: 28px;
    text-transform: capitalize;
  }

  .filters {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 30px;

    .text-input {
      width: 320px;

      input {
        border-radius: 4px;
        border-color: $alto;
      }

      .icon {
        top:20px;
      }
    }
  }

  .list {
    .item {
      background-color: $babyPowder;
      border-radius: 4px;
      padding: 15px;
      margin-left: -7.5px;
      margin-right: -7.5px;
      margin-bottom: 15px;
      position: relative;

      a {
        color: inherit;
        text-decoration: none;
      }

      h3 {
        font-size: 16px;
        font-weight: bold;
        text-transform: uppercase;
        display: flex;
        justify-content: space-between;
        color: $blueNcs;
        transition: all .2s ease-in-out;

        &:hover {
          color: $paradisePink;
        }
      }

      .info {
        table {
          tr {
            td {
              &:first-child {
                padding-right: 15px;
              }
            }
          }
        }
      }

      .call {
        position: absolute;
        bottom: 10px;
        right: 10px;
        border: 1px solid $alto;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        text-align: center;
        padding-top: 7px;
        cursor: pointer;

        color: $shamrockGreen;
        transition: all .2s ease-in-out;

        &:hover {
          color: $babyPowder;
          background-color: $shamrockGreen;
        }
      }
    }
  }
}
</style>
